import { methodLabel, type PaymentRegisterRow } from "@/api/payments";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField } from "@/components/ui/field";
import { formatDate, formatMoney } from "@/lib/format";

/** A shortfall to waive with the payment, cleared on the receipt. */
export type PaymentLine = { write_off: string };

/** The client's live payments that no receipt acknowledges yet, to tick into one receipt. */
export function PaymentPicker({
  id,
  payments,
  lines,
  onChange,
}: {
  id: string;
  payments: PaymentRegisterRow[];
  lines: Record<string, PaymentLine>;
  onChange: (lines: Record<string, PaymentLine>) => void;
}) {
  if (payments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No payment of this client is waiting for a receipt.
      </p>
    );
  }

  function tick(payment: PaymentRegisterRow, on: boolean): void {
    const kept = Object.fromEntries(Object.entries(lines).filter(([key]) => key !== payment.id));
    onChange(on ? { ...kept, [payment.id]: { write_off: "" } } : kept);
  }

  return (
    <ul className="divide-y rounded-md border">
      {payments.map((payment) => {
        const line = lines[payment.id];
        return (
          <li key={payment.id} className="px-3 py-2">
            <Checkbox
              id={`${id}-payment-${payment.id}`}
              label={`${formatMoney(payment.amount, payment.currency)} on ${payment.invoice_no ?? "an invoice"}, received ${formatDate(payment.received_on)}`}
              checked={line !== undefined}
              onChange={(on) => tick(payment, on)}
            />
            <p className="ml-6 text-xs text-muted-foreground">
              {methodLabel(payment.method)}
              {payment.received_currency && payment.received_amount
                ? ` · ${formatMoney(payment.received_amount, payment.received_currency)}`
                : ""}
              {payment.vessel_name ? ` · ${payment.vessel_name}` : ""}
            </p>
            {line && (
              <TextField
                id={`${id}-write-off-${payment.id}`}
                label={`Write-off on ${payment.invoice_no ?? "the invoice"}`}
                inputMode="decimal"
                value={line.write_off}
                onChange={(write_off) => onChange({ ...lines, [payment.id]: { write_off } })}
                hint="A shortfall you waive; it closes the invoice, so it must clear the rest"
                className="ml-6 mt-2 sm:max-w-xs"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
